import { FC, useCallback } from "react"
import { ExploreGptView } from "./ExploreGptView";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { useNotification } from "../../Contexts/NotificationContext";
import { ActionTypes, ConversationCreate, ConversationPatch, IGptConfiguration, IUserPreferences, UserPreferences } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { useConversations, useGptConfigurations, useUserPreferences } from "../../store/hooks";
import { setUserPreferencesAction } from "../../store/actions/userPreferencesAction";
import { t } from "i18next";
import { addConversationAction } from "../../store/actions/conversationActions";
import { useNavigate } from "react-router-dom";
import { buildRoute, ChatSpecificRoute } from "../../Routes";

const ExploreGptHandler: FC = () => {
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const { userPreferences, dispatch } = useUserPreferences();
    const { gptConfigurations } = useGptConfigurations();
    const { chats } = useConversations();

    const onGptAdd = useCallback((gpt: IGptConfiguration) => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                // Default GPTs are added automatically
                if (!gpt.isDefault) {
                    // use this piece of code, in case you want to override the limited user preferences also on the DB

                    // // Keep at most 4 GPTs (+ SyGPT) pinned
                    // if (userPreferences.addedGpts?.length! >= 4) {
                    //     // pick the last 3 added GPTs
                    //     userPreferences.addedGpts = userPreferences.addedGpts?.slice(-3);
                    // }
                    // Update user preferences
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        addedGpts: [...userPreferences.addedGpts!, gpt.id!]
                    };              
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    dispatch(setUserPreferencesAction(updatedUserPreferences));
                }

                // If the GPT is default (SyGPT), check for existing empty conversation and navigate to it
                if (gpt.isDefault) {
                    const emptyConversation = chats.find(c => c.gpt?.id === gpt.id && c.prompts?.length === 0);
                    if(emptyConversation) {
                        navigate(buildRoute(ChatSpecificRoute, { id: emptyConversation.id }));
                        return;
                    }
                }
                // If not found, create a new conversation with the GPT
                const conversationCreate = new ConversationCreate({ gpt: { id: gpt?.id, type: gpt?.type } });
                let newConversation = await client.createConversation(conversationCreate);
                dispatch(addConversationAction({ ...newConversation }));
                newConversation = await client.patchConversation(
                    newConversation.id,
                    new ConversationPatch({ actionType: ActionTypes.RenameConversation })
                );
                navigate(buildRoute(ChatSpecificRoute, { id: newConversation.id }));
            } catch (e) {
                dispatchNotification('error.api.post.conversation', 'error');
            }
        });
    }, [userPreferences]);

    const onGptRemove = useCallback((gpt: IGptConfiguration) => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                // Update user preferences
                const updatedUserPreferences: IUserPreferences = {
                    ...userPreferences,
                    addedGpts: userPreferences.addedGpts?.filter(x => x !== gpt.id)
                };
                await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                dispatch(setUserPreferencesAction(updatedUserPreferences));

            } catch (e) {
                dispatchNotification('error.api.patch.userPreferences', 'error');
            }
        });
    }, [userPreferences]);

    return <ExploreGptView gpts={gptConfigurations} onGptAdd={onGptAdd} onGptRemove={onGptRemove} />;
}

export default ExploreGptHandler;

